// src/features/hotels/components/HotelActions.tsx

import React, { useState } from 'react';
import {
    useTranslate,
    useRedirect,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Box,
    Divider,
} from '@mui/material';
import {
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Business as BusinessIcon,
    Language as LanguageIcon,
    Notifications as NotificationsIcon,
    Settings as SettingsIcon,
} from '@mui/icons-material';
import { useHotelPermissions } from '../hooks/useHotelPermissions';
import { Hotel } from '../types';
import BaseDeleteDialog from '../../../components/common/BaseDeleteDialog';

interface HotelActionsProps {
    record: Hotel;
    small?: boolean;
}

export const HotelActions: React.FC<HotelActionsProps> = ({ record, small = false }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const redirect = useRedirect();
    const permissions = useHotelPermissions(record);
    
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        redirect(`/hotels/${record.id}`);
    };

    const handleManageZones = () => {
        handleMenuClose();
        redirect(`/hotels/${record.id}/zones`);
    };

    const handleManageTexts = () => {
        handleMenuClose();
        redirect(`/hotels/${record.id}/texts`);
    };

    const handleManageNotifications = () => {
        handleMenuClose();
        redirect(`/hotels/${record.id}/notifications`);
    };

   
    const handleManageFields = () => {
        handleMenuClose();
        redirect(`/hotels/${record.id}/fields`);
    };

   
    const handleDeleteClick = () => {
        handleMenuClose();
        setShowDeleteDialog(true);
    };

    
    const handleDeleteSuccess = () => {
        setShowDeleteDialog(false);
        notify(
            translate('resources.hotels.notifications.deleted', 'Hotel eliminado con éxito'),
            { type: 'success' }
        );
        refresh();
    };

   
    const handleDeleteCancel = () => {
        setShowDeleteDialog(false);
    };

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title={translate('ra.action.menu', 'Más opciones')}>
                    <IconButton
                        size={small ? 'small' : 'medium'}
                        onClick={handleMenuOpen}
                        sx={{ color: 'action.active' }}
                    >
                        <MoreVertIcon fontSize={small ? 'small' : 'medium'} />
                    </IconButton>
                </Tooltip>
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                {/* Ver/Editar Hotel */}
                {permissions.canEdit && (
                    <MenuItem onClick={handleEdit}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText 
                            primary={translate('ra.action.edit', 'Editar')} 
                            secondary={translate('resources.hotels.actions.edit_desc', 'Modificar información básica')}
                        />
                    </MenuItem>
                )}
                {/* Sección de configuración */}
                {(permissions.canManageZones || permissions.canManageTexts || 
                  permissions.canManageNotifications || permissions.canManageFields) && (
                    <>
                        <Divider sx={{ my: 0.5 }} />
                        
                        {/* Gestionar Zonas */}
                        {permissions.canManageZones && (
                            <MenuItem onClick={handleManageZones}>
                                <ListItemIcon>
                                    <BusinessIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={translate('resources.hotels.actions.manage_zones', 'Gestionar Zonas')} 
                                    secondary={translate('resources.hotels.actions.manage_zones_desc', 'Configurar áreas del hotel')}
                                />
                            </MenuItem>
                        )}

                        {/* Gestionar Textos HMI */}
                        {permissions.canManageTexts && (
                            <MenuItem onClick={handleManageTexts}>
                                <ListItemIcon>
                                    <LanguageIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={translate('resources.hotels.actions.manage_texts', 'Textos HMI')} 
                                    secondary={translate('resources.hotels.actions.manage_texts_desc', 'Personalización multiidioma')}
                                />
                            </MenuItem>
                        )}

                        {/* Configurar Notificaciones */}
                        {permissions.canManageNotifications && (
                            <MenuItem onClick={handleManageNotifications}>
                                <ListItemIcon>
                                    <NotificationsIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={translate('resources.hotels.actions.manage_notifications', 'Notificaciones')} 
                                    secondary={translate('resources.hotels.actions.manage_notifications_desc', 'Alertas y reportes')}
                                />
                            </MenuItem>
                        )}

                        {/* Configurar Campos de Referencia */}
                        {permissions.canManageFields && (
                            <MenuItem onClick={handleManageFields}>
                                <ListItemIcon>
                                    <SettingsIcon fontSize="small" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={translate('resources.hotels.actions.manage_fields', 'Campos de Referencia')} 
                                    secondary={translate('resources.hotels.actions.manage_fields_desc', 'Tipos de identificación')}
                                />
                            </MenuItem>
                        )}
                    </>
                )}

                {/* Sección de administración */}
                {(permissions.canEdit || permissions.canDelete) && (
                    <>
                        <Divider sx={{ my: 0.5 }} />
                        {/* Eliminar */}
                        {permissions.canDelete && (
                            <MenuItem onClick={handleDeleteClick}>
                                <ListItemIcon>
                                    <DeleteIcon fontSize="small" color="error" />
                                </ListItemIcon>
                                <ListItemText 
                                    primary={translate('ra.action.delete', 'Eliminar')} 
                                    secondary={translate('resources.hotels.actions.delete_desc', 'Eliminar permanentemente')}
                                    sx={{ '& .MuiListItemText-primary': { color: 'error.main' } }}
                                />
                            </MenuItem>
                        )}
                    </>
                )}
            </Menu>

            {/* Diálogo de confirmación de eliminación */}
            {showDeleteDialog && (
                <BaseDeleteDialog
                    open={showDeleteDialog}
                    onClose={handleDeleteCancel}
                    onConfirm={handleDeleteSuccess}
                    resource="hotels"
                    recordId={record.id}
                    title={translate('resources.hotels.dialogs.delete_title', 'Eliminar Hotel')}
                    message={
                        record.lockerCount && record.lockerCount > 0
                            ? translate(
                                'resources.hotels.dialogs.delete_cascade_warning',
                                'Esta acción eliminará todas las zonas, taquillas y asignaciones asociadas.'
                            )
                            : undefined
                    }
                />
            )}
        </>
    );
};

export default HotelActions;